/**
 * Import planning. Nothing is written until the person confirms the plan.
 *
 * Merge rules, per record id:
 *  - id not in this device            → added
 *  - same id, same content            → skipped (no duplicate)
 *  - same id, different content       → conflict; the person picks "keep this device" (default) or "use the file"
 *  - id used by another kind of record → blocked; the import cannot proceed
 * "Replace all" swaps the whole library for the file, after an explicit confirmation.
 */
import { cloneLibrary } from "./library";
import type { Action, Category, EntityKind, Id, Library, Workspace } from "./types";

type AnyRec = Workspace | Category | Action;
export interface Conflict { kind: EntityKind; id: Id; localName: string; incomingName: string }
export interface ImportPlan {
  incoming: Library;
  inFile: { workspaces: number; categories: number; actions: number };
  toAdd: { workspaces: number; categories: number; actions: number };
  identical: number;
  conflicts: Conflict[];
  blocked: string[];
}
export type ConflictStrategy = "keep-local" | "use-file";

const KINDS: { kind: EntityKind; key: "workspaces" | "categories" | "actions" }[] = [
  { kind: "workspace", key: "workspaces" }, { kind: "category", key: "categories" }, { kind: "action", key: "actions" }
];
const CONTENT: Record<EntityKind, string[]> = {
  workspace: ["name", "icon"],
  category: ["name", "icon", "glow", "workspaceId"],
  action: ["name", "icon", "color", "type", "url", "text", "categoryId"]
};
const sameContent = (kind: EntityKind, a: AnyRec, b: AnyRec) =>
  CONTENT[kind].every(f => (a as any)[f] === (b as any)[f]);

export function planImport(local: Library, incoming: Library): ImportPlan {
  const localIndex = new Map<Id, { kind: EntityKind; rec: AnyRec }>();
  for (const { kind, key } of KINDS) for (const r of local[key]) localIndex.set(r.id, { kind, rec: r });

  const plan: ImportPlan = {
    incoming,
    inFile: { workspaces: incoming.workspaces.length, categories: incoming.categories.length, actions: incoming.actions.length },
    toAdd: { workspaces: 0, categories: 0, actions: 0 }, identical: 0, conflicts: [], blocked: []
  };
  for (const { kind, key } of KINDS) {
    for (const r of incoming[key] as AnyRec[]) {
      const hit = localIndex.get(r.id);
      if (!hit) plan.toAdd[key]++;
      else if (hit.kind !== kind) plan.blocked.push(`ID "${r.id}" ในไฟล์ (${r.name}) ซ้ำกับรายการคนละประเภทในเครื่องนี้`);
      else if (sameContent(kind, hit.rec, r)) plan.identical++;
      else plan.conflicts.push({ kind, id: r.id, localName: hit.rec.name, incomingName: r.name });
    }
  }
  return plan;
}

/** Merges the file into a copy of the local library. Ids are kept as they are; nothing is duplicated. */
export function applyMerge(local: Library, plan: ImportPlan, strategy: ConflictStrategy): Library {
  if (plan.blocked.length) throw new Error("import blocked");
  const out = cloneLibrary(local);
  const conflictIds = new Set(plan.conflicts.map(c => c.id));
  for (const { kind, key } of KINDS) {
    const list = out[key] as AnyRec[];
    const byId = new Map(list.map(r => [r.id, r]));
    // keep the file's order, appended after what is already in the same parent
    const sorted = [...(plan.incoming[key] as AnyRec[])].sort((a, b) => a.position - b.position);
    for (const r of sorted) {
      const existing = byId.get(r.id);
      if (!existing) {
        const parent = parentOf(kind, r);
        const pos = list.filter(x => parentOf(kind, x) === parent).reduce((m, x) => Math.max(m, x.position), -1) + 1;
        const added = { ...r, position: pos };
        list.push(added); byId.set(r.id, added);
      } else if (conflictIds.has(r.id) && strategy === "use-file") {
        const moved = parentOf(kind, existing) !== parentOf(kind, r);
        const keepPos = existing.position;
        Object.assign(existing, r);
        if (moved) {
          const parent = parentOf(kind, r);
          existing.position = list.filter(x => x !== existing && parentOf(kind, x) === parent).reduce((m, x) => Math.max(m, x.position), -1) + 1;
        } else existing.position = keepPos;
      }
    }
  }
  if (!out.workspaces.some(w => w.id === out.settings.activeWorkspaceId))
    out.settings.activeWorkspaceId = plan.incoming.settings.activeWorkspaceId ?? out.workspaces[0]?.id ?? null;
  return out;
}

export function applyReplace(plan: ImportPlan): Library {
  return cloneLibrary(plan.incoming);
}

function parentOf(kind: EntityKind, r: AnyRec): Id | null {
  return kind === "category" ? (r as Category).workspaceId : kind === "action" ? (r as Action).categoryId : null;
}
